import React from "react";

/* Shared, lightly-styled primitives for the template builder — kept in one
   place so every card looks and behaves the same. */

export function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ring-1 ring-black/[0.02] ${className}`}
    >
      {children}
    </div>
  );
}

export function Label({ children, required, hint }) {
  return (
    <label className="mb-1.5 flex items-baseline justify-between">
      <span className="text-[13px] font-medium text-slate-700">
        {children}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

export function Hint({ children }) {
  return <p className="mt-1.5 text-[11px] leading-4 text-slate-400">{children}</p>;
}

const inputBase =
  "w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#147db5] focus:ring-4 focus:ring-[#147db5]/10 disabled:bg-slate-50 disabled:text-slate-400";

export const TextInput = React.forwardRef(function TextInput(props, ref) {
  return (
    <input ref={ref} {...props} className={`${inputBase} ${props.className || ""}`} />
  );
});

export const TextArea = React.forwardRef(function TextArea(props, ref) {
  return (
    <textarea
      ref={ref}
      {...props}
      className={`${inputBase} resize-none leading-6 ${props.className || ""}`}
    />
  );
});

export function Select({ children, ...props }) {
  return (
    <select {...props} className={`${inputBase} pr-9 ${props.className || ""}`}>
      {children}
    </select>
  );
}

export function Counter({ value = 0, max }) {
  const over = max && value > max;
  return (
    <span
      className={`text-[11px] font-medium tabular-nums ${
        over ? "text-red-500" : "text-slate-400"
      }`}
    >
      {value}
      {max ? `/${max}` : ""}
    </span>
  );
}

/** A pill-style single-select. options: [{ value, label, icon?, description? }] */
export function Segmented({ options, value, onChange, columns = 2 }) {
  return (
    <div
      className="grid gap-2"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {options.map((opt) => {
        const Icon = opt.icon;
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`flex items-start gap-2.5 rounded-xl border p-3 text-left transition ${
              selected
                ? "border-[#147db5] bg-[#eaf4fa]/70 ring-1 ring-[#b9dff0]"
                : "border-slate-200 hover:border-[#7cc4e8] hover:bg-slate-50"
            }`}
          >
            {Icon && (
              <span
                className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
                  selected ? "bg-[#147db5] text-white" : "bg-slate-100 text-slate-500"
                }`}
              >
                <Icon size={16} />
              </span>
            )}
            <span className="min-w-0">
              <span className="block text-[13px] font-semibold text-slate-800">
                {opt.label}
              </span>
              {opt.description && (
                <span className="mt-0.5 block text-[11px] leading-4 text-slate-500">
                  {opt.description}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function Toggle({ checked, onChange, label, description }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3.5 py-3 text-left transition hover:bg-slate-50"
    >
      <span>
        <span className="block text-[13px] font-medium text-slate-800">{label}</span>
        {description && (
          <span className="mt-0.5 block text-[11px] text-slate-500">{description}</span>
        )}
      </span>
      <span
        className={`relative h-5 w-9 flex-shrink-0 rounded-full transition ${
          checked ? "bg-[#147db5]" : "bg-slate-300"
        }`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition ${
            checked ? "left-4" : "left-0.5"
          }`}
        />
      </span>
    </button>
  );
}
